import { useMemo, useState } from 'react';
import { ArrowRight, CalendarDays, Clock, Download, LineChart, TrendingDown, TrendingUp, Zap } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { SupernovaDatePicker } from '@/components/ui/date-picker/supernova-date-picker';
import type { DateRange } from '@/components/ui/date-picker/types';
import { useLocationStore } from '@/stores/location-store';
import { useReportingTimeZone } from '@/hooks/use-reporting-timezone';
import { usePermissions } from '@/hooks/use-permissions';
import { usePosTotalDailySales, type PosTotalDailySalesRow } from '@/hooks/use-pos-reports';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';
import { downloadCsv, reportFilename } from '@/lib/report-csv';
import {
  dailyInputToDate,
  dateToDailyInput,
  defaultDailyRangeWithTime,
  initialDailyRange,
  type ReportRange,
} from '@/lib/report-range';
import { CardSkeleton, DeltaBadge, KpiCard, SalesTrend } from '@/pages/dashboard/home/home-cards';
import { EmptyState } from '@/components/shared/empty-state';
import {
  bucketTrend,
  fillHours,
  formatTrendBucket,
  lastHourOf,
  previousRange,
  shiftDays,
  spanDays,
  toDelta,
  trendGranularityFor,
  type TrendGranularity,
  type TrendPoint,
} from '@/pages/dashboard/home/use-home-analytics';

/**
 * Reports → POS → Sales → Sales Trends.
 *
 * The drill-down behind the home page's Sales Trends card. It keeps the card's
 * two decisions — the period, and how that period is bucketed (hour / day /
 * week / month, see trendGranularityFor) — so the numbers here are the numbers
 * the user just looked at, then adds what a card has no room for: the same
 * period before this one for comparison, a breakdown table that reconciles to
 * Total Daily Sales, and a few derived facts (best week, peak hour, …).
 *
 * Everything is computed from the Total Daily Sales endpoint, fetched once for
 * the range and once for the preceding range. No backend of its own.
 */

/** One bucket: the home card's point plus the money columns Total Daily Sales
 *  reports, so the table below reconciles to that report line for line. */
type TrendRow = TrendPoint & {
  tax: number;
  fees: number;
  discounts: number;
  tips: number;
  /** Same formula as Total Daily Sales' "Total Billed Amount": sales + tax +
   *  fees + tips − discounts. Refunds are shown beside it, not netted off. */
  net: number;
};

const GROUPINGS: Array<{ key: TrendGranularity; label: string }> = [
  { key: 'hour', label: 'Hourly' },
  { key: 'day', label: 'Daily' },
  { key: 'week', label: 'Weekly' },
  { key: 'month', label: 'Monthly' },
];
const TITLE: Record<TrendGranularity, string> = { hour: 'Hourly', day: 'Daily', week: 'Weekly', month: 'Monthly' };
const NOUN: Record<TrendGranularity, string> = { hour: 'Hour', day: 'Day', week: 'Week', month: 'Month' };
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function toRows(daily: PosTotalDailySalesRow[]): TrendRow[] {
  return [...daily]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((r) => {
      const net = r.sales + r.tax + r.fees + r.tips - r.discounts;
      return {
        date: r.date,
        sales: r.sales,
        transactions: r.ordersCount,
        refunds: r.refundedAmount,
        avgBasket: r.ordersCount > 0 ? net / r.ordersCount : 0,
        tax: r.tax,
        fees: r.fees,
        discounts: r.discounts,
        tips: r.tips,
        net,
      };
    });
}

interface Totals {
  orders: number;
  sales: number;
  discounts: number;
  tax: number;
  fees: number;
  tips: number;
  net: number;
  refunds: number;
  basket: number;
}

function sumRows(rows: TrendRow[]): Totals {
  const t: Totals = { orders: 0, sales: 0, discounts: 0, tax: 0, fees: 0, tips: 0, net: 0, refunds: 0, basket: 0 };
  for (const r of rows) {
    t.orders += r.transactions;
    t.sales += r.sales;
    t.discounts += r.discounts;
    t.tax += r.tax;
    t.fees += r.fees;
    t.tips += r.tips;
    t.net += r.net;
    t.refunds += r.refunds;
  }
  t.basket = t.orders > 0 ? t.net / t.orders : 0;
  return t;
}

/** "Mon, Sep 14, 2026" for a single day, "Aug 1 – Aug 31, 2026" otherwise. */
function rangeLabel(range: ReportRange): string {
  const civil = (day: string) => {
    const [y, m, d] = day.slice(0, 10).split('-').map(Number);
    return y && m && d ? new Date(Date.UTC(y, m - 1, d)) : null;
  };
  const from = civil(range.dateFrom);
  const to = civil(range.dateTo);
  if (!from || !to) return `${range.dateFrom.slice(0, 10)} – ${range.dateTo.slice(0, 10)}`;
  const fmt = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'UTC' });
  if (range.dateFrom.slice(0, 10) === range.dateTo.slice(0, 10)) {
    return fmt({ weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }).format(from);
  }
  const md = fmt({ month: 'short', day: 'numeric' });
  return `${md.format(from)} – ${md.format(to)}, ${to.getUTCFullYear()}`;
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-4 rounded-sm border-t-2 border-primary bg-primary/15" />
        Net sales
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="w-4 border-t-2 border-dashed border-muted-foreground" />
        Transactions
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="w-4 border-t-2 border-dotted border-primary/50" />
        Previous period
      </span>
    </div>
  );
}

function Insight({
  icon: Icon,
  label,
  headline,
  detail,
}: {
  icon: React.ElementType;
  label: string;
  headline: string;
  detail: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border bg-white px-4 py-3.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
        <Icon className="h-4 w-4 text-primary" />
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-sm font-semibold text-foreground">{headline}</span>
        <span className="text-xs text-muted-foreground">{detail}</span>
      </div>
    </div>
  );
}

const TH = 'px-3 py-2.5 font-medium whitespace-nowrap';
const TD = 'px-3 py-2.5 tabular-nums whitespace-nowrap';

export function PosSalesTrendsReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();
  const canExport = usePermissions().has('reports.pos.export');

  // The picker edits `inputs`; Apply copies them into `applied`, which is what
  // the queries and every derived figure key off — same two-step as the other
  // report pages, so a half-picked range never fires a request.
  const [inputs, setInputs] = useState<ReportRange>(() => initialDailyRange(reportTz));
  const [applied, setApplied] = useState<ReportRange>(inputs);
  const [groupBy, setGroupBy] = useState<'auto' | TrendGranularity>('auto');
  const [compare, setCompare] = useState(true);

  const auto = trendGranularityFor(applied);
  const singleDay = spanDays(applied) <= 1;
  // Hourly is only offered for a single day: hour keys carry no date, and a
  // month of hours is 700+ points nobody can read. An override that stops
  // making sense after the range changes falls back to the automatic choice.
  const granularity: TrendGranularity = groupBy === 'auto' || (groupBy === 'hour' && !singleDay) ? auto : groupBy;
  const fetchGranularity = granularity === 'hour' ? 'hour' : 'day';
  const prev = useMemo(() => previousRange(applied), [applied]);

  const scope = { locationId: selectedLocationId || undefined };
  const current = usePosTotalDailySales({ ...scope, ...applied, granularity: fetchGranularity });
  const previous = usePosTotalDailySales({ ...scope, ...prev, granularity: fetchGranularity }, { enabled: compare });

  const dailyRows = useMemo(() => toRows(current.data?.data?.daily ?? []), [current.data]);

  const series = useMemo(() => {
    let rows = dailyRows;
    if (granularity === 'hour') {
      const day = applied.dateFrom.slice(0, 10);
      rows = fillHours(rows, day, lastHourOf(day, reportTz));
    }
    return bucketTrend(rows, granularity, applied);
  }, [dailyRows, granularity, applied, reportTz]);

  /**
   * The previous period, moved forward by the range length so it buckets on
   * the current range's boundaries and lines up point for point: week 1 against
   * the first days of last month, 9 AM against 9 AM yesterday. For a single day
   * the previous day is filled through midnight, so today's chart carries on
   * past the current hour with yesterday's remaining shape.
   */
  const prevSeries = useMemo(() => {
    if (!compare) return undefined;
    const shift = spanDays(applied);
    let rows = toRows(previous.data?.data?.daily ?? []).map((r) => ({ ...r, date: shiftDays(r.date, shift) }));
    if (granularity === 'hour') rows = fillHours(rows, applied.dateFrom.slice(0, 10), 23);
    return bucketTrend(rows, granularity, applied);
  }, [compare, previous.data, granularity, applied]);

  // Only the part of the previous period that today has reached is comparable
  // (a partial day against all of yesterday would always read as "behind").
  // For any finished period the two are the same length.
  const comparable = useMemo(() => prevSeries?.slice(0, series.length) ?? null, [prevSeries, series.length]);
  const totals = useMemo(() => sumRows(series), [series]);
  const prevTotals = useMemo(() => (comparable && comparable.length > 0 ? sumRows(comparable) : null), [comparable]);
  const partial = !!prevSeries && prevSeries.length > series.length;
  const prevLabel = rangeLabel(prev);
  const vsLabel = partial
    ? `vs ${prevLabel} to ${formatTrendBucket(series[series.length - 1]?.date ?? '')}`
    : `vs ${prevLabel}`;

  const insights = useMemo(() => {
    const withSales = series.filter((r) => r.transactions > 0);
    if (withSales.length === 0) return [];
    const best = withSales.reduce((a, b) => (b.net > a.net ? b : a));
    const worst = withSales.reduce((a, b) => (b.net < a.net ? b : a));
    const noun = NOUN[granularity].toLowerCase();
    const fact = (r: TrendRow) => `${formatCurrency(r.net)} net · ${formatNumber(r.transactions)} orders`;
    const items: Array<{ icon: React.ElementType; label: string; headline: string; detail: string }> = [
      {
        icon: granularity === 'hour' ? Zap : TrendingUp,
        label: granularity === 'hour' ? 'Peak hour' : `Best ${noun}`,
        headline: formatTrendBucket(best.date, true),
        detail: fact(best),
      },
    ];
    if (withSales.length > 1) {
      items.push({
        icon: TrendingDown,
        label: granularity === 'hour' ? 'Quietest hour' : `Slowest ${noun}`,
        headline: formatTrendBucket(worst.date, true),
        detail: fact(worst),
      });
    }
    if (granularity === 'hour') {
      if (prevTotals && prevTotals.net > 0) {
        const pct = ((totals.net - prevTotals.net) / prevTotals.net) * 100;
        items.push({
          icon: Clock,
          label: 'On pace',
          headline: `Tracking ${Math.abs(pct).toFixed(1)}% ${pct >= 0 ? 'ahead' : 'behind'}`,
          detail: vsLabel,
        });
      }
    } else {
      // Weekday mix comes from the unbucketed days, whatever the grouping.
      const byDay = new Array<number>(7).fill(0);
      let total = 0;
      for (const r of dailyRows) {
        const dow = new Date(`${r.date.slice(0, 10)}T00:00:00Z`).getUTCDay();
        byDay[dow] += r.net;
        total += r.net;
      }
      if (total > 0) {
        const top = byDay.indexOf(Math.max(...byDay));
        items.push({
          icon: CalendarDays,
          label: 'Busiest day of the week',
          headline: WEEKDAYS[top],
          detail: `${((byDay[top] / total) * 100).toFixed(0)}% of net sales`,
        });
      }
    }
    return items;
  }, [series, dailyRows, granularity, totals.net, prevTotals, vsLabel]);

  const handleApply = () => {
    if (!inputs.dateFrom || !inputs.dateTo) return;
    setApplied(inputs);
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setInputs(r);
    setApplied(r);
    setGroupBy('auto');
    setCompare(true);
  };

  const exportCsv = () => {
    const header = [
      'Location',
      NOUN[granularity],
      'Orders',
      'Sales ($)',
      'Discounts ($)',
      'Tax ($)',
      'Fees ($)',
      'Tips ($)',
      'Net Sales ($)',
      'Refunded Amount ($)',
      ...(comparable ? ['Previous Net Sales ($)', 'vs Previous (%)'] : []),
    ];
    const body = series.map((r, i) => {
      const p = comparable?.[i];
      return [
        selectedLocationName || 'All locations',
        formatTrendBucket(r.date, true),
        r.transactions,
        r.sales.toFixed(2),
        r.discounts.toFixed(2),
        r.tax.toFixed(2),
        r.fees.toFixed(2),
        r.tips.toFixed(2),
        r.net.toFixed(2),
        r.refunds.toFixed(2),
        ...(comparable
          ? [p ? p.net.toFixed(2) : '', p && p.net > 0 ? (((r.net - p.net) / p.net) * 100).toFixed(1) : '']
          : []),
      ];
    });
    downloadCsv(reportFilename('sales_trends', selectedLocationName), [header, ...body]);
  };

  const query = `&from=${encodeURIComponent(applied.dateFrom)}&to=${encodeURIComponent(applied.dateTo)}`;
  const loading = current.isLoading || (compare && previous.isLoading);
  const noSales = !loading && series.every((r) => r.transactions === 0 && r.sales === 0);

  return (
    <div className="space-y-4">
      <PageHeader
        title={`Sales Trends (${TITLE[granularity]})`}
        description="How sales moved across the period you picked, grouped to fit the range."
        onBack={onBack}
      >
        {canExport && (
          <Button variant="primary" size="sm" disabled={series.length === 0} onClick={exportCsv}>
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        )}
      </PageHeader>

      {/* Filters — the same card the other report pages open with, plus the
          two controls this page adds: grouping and the comparison toggle. */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range &amp; Grouping</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Period</label>
            <SupernovaDatePicker
              mode="range"
              presetFirst
              portal
              compactDisplay
              autoApply={false}
              timezone={reportTz}
              className="w-[300px] max-w-full"
              value={{
                start: dailyInputToDate(inputs.dateFrom, reportTz),
                end: dailyInputToDate(inputs.dateTo, reportTz),
              }}
              onApply={(v) => {
                const r = (v as DateRange | null) ?? { start: null, end: null };
                setInputs({ dateFrom: dateToDailyInput(r.start, reportTz), dateTo: dateToDailyInput(r.end, reportTz) });
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">
              Group by <span className="text-muted-foreground/70">· picked for this range, choose to override</span>
            </label>
            <div
              role="radiogroup"
              aria-label="Group by"
              className="inline-flex h-9 items-center gap-0.5 rounded-lg bg-muted p-0.5"
            >
              {GROUPINGS.map((g) => {
                const disabled = g.key === 'hour' && !singleDay;
                const active = g.key === granularity;
                return (
                  <button
                    key={g.key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={disabled}
                    title={disabled ? 'Hourly is available for a single day' : undefined}
                    onClick={() => setGroupBy(g.key === auto ? 'auto' : g.key)}
                    className={cn(
                      'h-8 rounded-md px-3 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                      active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {g.label}
                    {active && groupBy === 'auto' && <span className="ml-1 font-normal opacity-80">· auto</span>}
                  </button>
                );
              })}
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={compare}
            onClick={() => setCompare((c) => !c)}
            className="flex h-9 items-center gap-2 text-sm text-foreground"
          >
            <span
              className={cn(
                'relative inline-block h-5 w-9 shrink-0 rounded-full transition-colors',
                compare ? 'bg-primary' : 'bg-slate-300',
              )}
            >
              <span
                className={cn(
                  'absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all',
                  compare ? 'left-[18px]' : 'left-0.5',
                )}
              />
            </span>
            <span>
              Compare with previous period <span className="text-muted-foreground">({prevLabel})</span>
            </span>
          </button>
          <div className="ml-auto flex gap-2">
            <Button variant="primary" size="md" onClick={handleApply}>
              Apply
            </Button>
            <Button variant="secondary" size="md" onClick={handleReset}>
              Reset
            </Button>
          </div>
        </div>
      </div>

      {current.isError ? (
        <div className="rounded-xl border border-border bg-white p-8 text-center text-sm text-muted-foreground">
          Couldn't load sales for this period. The Total Daily Sales report this page is built on may be hidden for your
          role at this location.
        </div>
      ) : (
        <>
          {/* ── headline figures ─────────────────────────────────────── */}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {loading ? (
              <>
                <CardSkeleton height="h-36" />
                <CardSkeleton height="h-36" />
                <CardSkeleton height="h-36" />
                <CardSkeleton height="h-36" />
              </>
            ) : (
              <>
                <KpiCard
                  label="Net Sales"
                  value={formatCurrency(totals.net)}
                  delta={prevTotals ? toDelta(totals.net, prevTotals.net) : undefined}
                  previous={prevTotals ? formatCurrency(prevTotals.net) : undefined}
                  footnote={prevTotals ? `${vsLabel} (${formatCurrency(prevTotals.net)})` : undefined}
                  series={series}
                  dataKey="net"
                />
                <KpiCard
                  label="Transactions"
                  value={formatNumber(totals.orders)}
                  delta={prevTotals ? toDelta(totals.orders, prevTotals.orders) : undefined}
                  footnote={prevTotals ? `${vsLabel} (${formatNumber(prevTotals.orders)})` : undefined}
                  series={series}
                  dataKey="transactions"
                />
                <KpiCard
                  label="Average Basket"
                  value={formatCurrency(totals.basket)}
                  delta={prevTotals ? toDelta(totals.basket, prevTotals.basket) : undefined}
                  footnote={prevTotals ? `${vsLabel} (${formatCurrency(prevTotals.basket)})` : undefined}
                  series={series}
                  dataKey="avgBasket"
                />
                <KpiCard
                  label="Refunds"
                  value={formatCurrency(totals.refunds)}
                  delta={prevTotals ? toDelta(totals.refunds, prevTotals.refunds) : undefined}
                  footnote={prevTotals ? `${vsLabel} (${formatCurrency(prevTotals.refunds)})` : undefined}
                  series={series}
                  dataKey="refunds"
                  invert
                />
              </>
            )}
          </div>

          {/* ── trend ────────────────────────────────────────────────── */}
          <section className="rounded-xl border border-border bg-white p-4">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Net Sales by {NOUN[granularity]}</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">{rangeLabel(applied)}</p>
              </div>
              <Legend />
            </div>
            {loading ? (
              <CardSkeleton height="h-56" />
            ) : (
              <SalesTrend series={series} previous={prevSeries} dataKey="net" label="Net sales" />
            )}
          </section>

          {/* ── derived facts ────────────────────────────────────────── */}
          {!loading && insights.length > 0 && (
            <div className="grid gap-4 md:grid-cols-3">
              {insights.map((i) => (
                <Insight key={i.label} {...i} />
              ))}
            </div>
          )}

          {/* ── breakdown ────────────────────────────────────────────── */}
          <div className="overflow-hidden rounded-xl border border-border bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground">{TITLE[granularity]} breakdown</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  One row per {NOUN[granularity].toLowerCase()}, matching the chart. Net Sales = Sales + Tax + Fees +
                  Tips − Discounts, as on Total Daily Sales. Export uses the same grouping.
                </p>
              </div>
              <a
                href={`#/reports?tab=pos&report=total-daily-sales${query}`}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                Open Total Daily Sales
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>
            <div className="table-scroll overflow-x-auto">
              <table className="w-full text-[0.8125rem]">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                    <th className={TH}>{NOUN[granularity]}</th>
                    <th className={cn(TH, 'text-right')}>Orders</th>
                    <th className={cn(TH, 'text-right')}>Sales</th>
                    <th className={cn(TH, 'text-right')}>Discounts</th>
                    <th className={cn(TH, 'text-right')}>Tax</th>
                    <th className={cn(TH, 'text-right')}>Fees</th>
                    <th className={cn(TH, 'text-right')}>Tips</th>
                    <th className={cn(TH, 'text-right')}>Net Sales</th>
                    <th className={cn(TH, 'text-right')}>Refunds</th>
                    {comparable && <th className={cn(TH, 'text-right')}>vs previous</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={comparable ? 10 : 9} className="px-4 py-10 text-center text-muted-foreground">
                        Loading…
                      </td>
                    </tr>
                  ) : noSales ? (
                    <tr>
                      <td colSpan={comparable ? 10 : 9}>
                        <EmptyState icon={LineChart} title="No sales recorded" description="Try a different period." />
                      </td>
                    </tr>
                  ) : (
                    <>
                      {series.map((r, i) => {
                        const p = comparable?.[i];
                        const d = p ? toDelta(r.net, p.net) : null;
                        return (
                          <tr key={r.date} className="border-b border-border/60">
                            <td className={cn(TD, 'font-medium text-foreground')}>{formatTrendBucket(r.date, true)}</td>
                            <td className={cn(TD, 'text-right')}>{formatNumber(r.transactions)}</td>
                            <td className={cn(TD, 'text-right')}>{formatCurrency(r.sales)}</td>
                            <td className={cn(TD, 'text-right')}>{formatCurrency(r.discounts)}</td>
                            <td className={cn(TD, 'text-right')}>{formatCurrency(r.tax)}</td>
                            <td className={cn(TD, 'text-right')}>{formatCurrency(r.fees)}</td>
                            <td className={cn(TD, 'text-right')}>{formatCurrency(r.tips)}</td>
                            <td className={cn(TD, 'text-right font-semibold text-foreground')}>
                              {formatCurrency(r.net)}
                            </td>
                            <td className={cn(TD, 'text-right')}>{formatCurrency(r.refunds)}</td>
                            {comparable && (
                              <td className={cn(TD, 'text-right')}>
                                {d && d.pct !== null ? (
                                  <DeltaBadge delta={d} />
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                      <tr className="bg-muted/50 font-semibold text-foreground">
                        <td className={TD}>Total</td>
                        <td className={cn(TD, 'text-right')}>{formatNumber(totals.orders)}</td>
                        <td className={cn(TD, 'text-right')}>{formatCurrency(totals.sales)}</td>
                        <td className={cn(TD, 'text-right')}>{formatCurrency(totals.discounts)}</td>
                        <td className={cn(TD, 'text-right')}>{formatCurrency(totals.tax)}</td>
                        <td className={cn(TD, 'text-right')}>{formatCurrency(totals.fees)}</td>
                        <td className={cn(TD, 'text-right')}>{formatCurrency(totals.tips)}</td>
                        <td className={cn(TD, 'text-right')}>{formatCurrency(totals.net)}</td>
                        <td className={cn(TD, 'text-right')}>{formatCurrency(totals.refunds)}</td>
                        {comparable && (
                          <td className={cn(TD, 'text-right')}>
                            {prevTotals && prevTotals.net > 0 ? (
                              <DeltaBadge delta={toDelta(totals.net, prevTotals.net)} />
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {!loading && noSales && series.length === 0 && (
            <EmptyState
              icon={LineChart}
              title="No sales recorded"
              description="Try a different period."
              className="py-8"
            />
          )}
        </>
      )}
    </div>
  );
}
